import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import Swal from 'sweetalert2';

import { PointOfSaleService } from '../../../../../services/pos';
import { HomeService } from '../../../../../services/home.service';

type DayKey =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

interface ClinicUser {
  id: number;
  name?: string;
  name_en?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  image?: string;
  image_url?: string;
  photo_url?: string;
  [key: string]: any;
}

interface OpeningShift {
  start: string;
  end: string;
  user_ids: number[];
  users?: ClinicUser[];
}

interface DaySchedule {
  day: DayKey;
  enabled: boolean;
  is24Hours: boolean;
  shifts: OpeningShift[];
}

interface BackendOpeningHour {
  id?: number;
  warehouse_id?: number;
  day: DayKey;
  is_open: boolean | number | string;
  is_24_hours?: boolean | number | string;
  is24hours?: boolean | number | string;
  opens_at?: string | null;
  closes_at?: string | null;
  shift_order?: number;
  shifts?: any;
}

@Component({
  selector: 'app-clinic-emergency-shift',
  templateUrl: './clinic-emergency-shift.component.html',
  styleUrl: './clinic-emergency-shift.component.scss'
})
export class ClinicEmergencyShiftComponent implements OnInit {
  warehouseId!: string | number;

  isAr = localStorage.getItem('language') === 'ar';

  readonly hourOptions = Array.from({ length: 12 }, (_, index) => index + 1);
  readonly minuteOptions = Array.from({ length: 60 }, (_, index) =>
    index.toString().padStart(2, '0')
  );

  selectedBusinessName = 'Business';

  loading = false;
  saving = false;
  submitted = false;
  serverError = '';

  usersLoading = false;
  usersLoadError = '';
  availableUsers: ClinicUser[] = [];
  productname: ClinicUser[] = [];
  filteredProviders: ClinicUser[] = [];
  userSearchValue = '';

  usersPopupOpen = false;
  popupDay: DaySchedule | null = null;
  popupShiftIndex = -1;
  popupTempUserIds: number[] = [];

  hasSavedSchedule = false;
  viewMode: 'form' | 'table' = 'form';

  savedSchedule: DaySchedule[] = [];
  draftSchedule: DaySchedule[] = this.createDefaultSchedule();
  overrideDate = this.formatDateOnly(new Date());
  overrideReason = '';
  effectiveSource: 'default' | 'override' = 'default';

  private usersMap = new Map<number, ClinicUser>();

  private readonly daysOrder: DayKey[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday'
  ];

  constructor(
    private posService: PointOfSaleService,
    private homeService: HomeService,
    private route: ActivatedRoute,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    const posId =
      this.route.snapshot.queryParamMap.get('warehouse_id') ||
      this.route.snapshot.queryParamMap.get('warehouseId') ||
      this.route.snapshot.paramMap.get('warehouse_id') ||
      this.route.snapshot.paramMap.get('warehouseId') ||
      this.route.snapshot.paramMap.get('id') ||
      localStorage.getItem('posId');

    if (!posId) {
      this.serverError = this.t('messages.noSelectedBusiness');
      this.viewMode = 'form';
      this.hasSavedSchedule = false;
      this.draftSchedule = this.createDefaultSchedule();
      return;
    }

    this.warehouseId = posId;

    this.loadWarehouseUsers();
    this.getOpeningHours();
  }

  getOpeningHours(): void {
    this.loading = true;
    this.serverError = '';

    this.posService.getEffectiveOpeningHour(this.warehouseId, this.overrideDate).subscribe({
      next: (res: any) => {
        const effectiveOpeningHour = this.extractEffectiveOpeningHour(res);

        this.selectedBusinessName =
          res?.warehouse_name ||
          res?.warehouse?.name ||
          res?.data?.warehouse_name ||
          res?.data?.warehouse?.name ||
          res?.warehouse?.name_en ||
          this.selectedBusinessName;

        const todaySchedule = this.normalizeEffectiveDay(effectiveOpeningHour);
        this.savedSchedule = [todaySchedule];
        this.draftSchedule = this.cloneSchedule(this.savedSchedule);
        this.hasSavedSchedule = false;
        this.viewMode = 'form';
        this.effectiveSource = this.extractEffectiveSource(res);
        this.overrideReason = effectiveOpeningHour?.reason || res?.data?.reason || res?.reason || '';

        this.loading = false;
      },
      error: (error) => {
        this.loadBaseOpeningHourForOverride(error);
      }
    });
  }

  private loadBaseOpeningHourForOverride(originalError?: any): void {
    this.posService.getOpeningHours(this.warehouseId).subscribe({
      next: (res: any) => {
        const schedule = this.extractScheduleFromResponse(res);
        const day = this.getDayKeyFromDate(this.parseDateOnly(this.overrideDate) || new Date());
        const baseDay = schedule.find((item) => item?.day === day);

        this.selectedBusinessName =
          res?.warehouse_name ||
          res?.warehouse?.name ||
          res?.data?.warehouse_name ||
          res?.data?.warehouse?.name ||
          res?.warehouse?.name_en ||
          this.selectedBusinessName;

        this.savedSchedule = [baseDay ? this.normalizeEffectiveDay(baseDay) : this.createDefaultDaySchedule(day)];
        this.draftSchedule = this.cloneSchedule(this.savedSchedule);
        this.hasSavedSchedule = false;
        this.viewMode = 'form';
        this.effectiveSource = 'default';
        this.overrideReason = '';
        this.serverError = '';
        this.loading = false;
      },
      error: (fallbackError) => {
        this.loading = false;
        this.hasSavedSchedule = false;
        this.viewMode = 'form';
        this.effectiveSource = 'default';
        this.savedSchedule = [this.createDefaultDaySchedule(this.getDayKeyFromDate(new Date()))];
        this.draftSchedule = this.cloneSchedule(this.savedSchedule);

        this.serverError =
          fallbackError?.error?.message ||
          originalError?.error?.message ||
          this.t('messages.loadFailed');
      }
    });
  }

  loadWarehouseUsers(): void {
    this.usersLoading = true;
    this.usersLoadError = '';

    const currentWarehouseId = Number(this.warehouseId);

    this.homeService.getAllUsers().subscribe({
      next: (res: any) => {
        const users = this.extractUsersFromResponse(res)
          .filter((user: any) => String(user.type) !== '9')
          .filter((user: any) => this.userBelongsToWarehouse(user, currentWarehouseId))
          .map((user: any) => ({
            ...user,
            id: Number(user.id)
          }))
          .filter((user: ClinicUser) => !Number.isNaN(Number(user.id)))
          .sort((a: ClinicUser, b: ClinicUser) => {
            const aName = String(a.last_name || a.first_name || a.name || '').trim();
            const bName = String(b.last_name || b.first_name || b.name || '').trim();
            return aName.localeCompare(bName, 'ar', { sensitivity: 'base' });
          });

        this.productname = users;
        this.availableUsers = users;
        this.filteredProviders = [...users];
        this.registerUsers(users);

        if (!users.length) {
          this.usersLoadError = this.isAr
            ? 'لا يوجد موظفين مرتبطين بهذا المخزن'
            : 'No employees found for this warehouse';
        }

        this.usersLoading = false;
      },
      error: () => {
        this.productname = [];
        this.availableUsers = [];
        this.filteredProviders = [];
        this.usersLoading = false;
        this.usersLoadError = this.isAr
          ? 'تعذر تحميل المستخدمين'
          : 'Failed to load users';
      }
    });
  }

  openFormForEdit(): void {
    this.submitted = false;
    this.serverError = '';
    this.draftSchedule = this.cloneSchedule(this.savedSchedule);
    this.viewMode = 'form';
  }

  cancelEdit(): void {
    this.submitted = false;
    this.serverError = '';

    if (this.hasSavedSchedule) {
      this.draftSchedule = this.cloneSchedule(this.savedSchedule);
      this.viewMode = 'table';
    } else {
      this.draftSchedule = this.createDefaultSchedule();
      this.viewMode = 'form';
    }
  }

  toggleDay(day: DaySchedule): void {
    day.enabled = !day.enabled;

    if (day.enabled && day.shifts.length === 0) {
      day.is24Hours = false;
      day.shifts = [
        {
          start: '10:00',
          end: '22:00',
          user_ids: []
        }
      ];
    }

    if (!day.enabled) {
      day.is24Hours = false;
      day.shifts = [];
    }
  }

  toggle24Hours(day: DaySchedule): void {
    if (!day.enabled) return;

    day.is24Hours = !day.is24Hours;

    const currentUserIds = this.uniqueIds(
      day.shifts.flatMap((shift) => shift.user_ids || [])
    );

    if (day.is24Hours) {
      day.shifts = [
        {
          start: '00:00',
          end: '23:59',
          user_ids: currentUserIds,
          users: this.getUsersByIds(currentUserIds)
        }
      ];

      return;
    }

    day.shifts = [
      {
        start: '10:00',
        end: '22:00',
        user_ids: currentUserIds,
        users: this.getUsersByIds(currentUserIds)
      }
    ];
  }

  addShift(day: DaySchedule): void {
    if (!day.enabled) {
      day.enabled = true;
    }

    if (day.is24Hours) {
      return;
    }

    day.shifts.push({
      start: '10:00',
      end: '22:00',
      user_ids: []
    });
  }

  removeShift(day: DaySchedule, shiftIndex: number): void {
    if (day.is24Hours) {
      return;
    }

    day.shifts.splice(shiftIndex, 1);

    if (day.shifts.length === 0) {
      day.enabled = false;
      day.is24Hours = false;
    }
  }

  updateShift(
    day: DaySchedule,
    shiftIndex: number,
    field: 'start' | 'end',
    event: Event
  ): void {
    const value = (event.target as HTMLInputElement).value;
    day.shifts[shiftIndex][field] = value;
  }

  updateShiftUsers(
    day: DaySchedule,
    shiftIndex: number,
    selectedIds: Array<number | string> | null | undefined
  ): void {
    const uniqueSelectedIds = this.uniqueIds(
      (selectedIds || [])
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id))
    );

    if (!day.shifts[shiftIndex]) return;

    day.shifts[shiftIndex].user_ids = uniqueSelectedIds;
    day.shifts[shiftIndex].users = this.getUsersByIds(uniqueSelectedIds);
  }

  updateArabicTimePart(
    day: DaySchedule,
    shiftIndex: number,
    field: 'start' | 'end',
    part: 'hour' | 'minute' | 'period',
    event: Event
  ): void {
    const value = (event.target as HTMLSelectElement).value;
    const currentTime = day.shifts[shiftIndex][field] || '10:00';
    const [hourText, minuteText] = currentTime.split(':');
    const currentHour = Number(hourText);
    const currentMinute = minuteText || '00';

    let hour12 = this.getTimeHour12(currentTime);
    let minute = currentMinute;
    let period = currentHour < 12 ? 'am' : 'pm';

    if (part === 'hour') {
      hour12 = Number(value);
    }

    if (part === 'minute') {
      minute = value.padStart(2, '0');
    }

    if (part === 'period') {
      period = value;
    }

    day.shifts[shiftIndex][field] = `${this.to24Hour(hour12, period)}:${minute}`;
  }

  onSubmit(): void {
    this.submitted = true;
    this.serverError = '';

    if (!this.isScheduleValid() || this.saving) {
      return;
    }

    const today = this.draftSchedule[0];
    const isOpen = today.enabled;
    const is24Hours = isOpen && today.is24Hours;
    const payload = {
      date: this.overrideDate,
      is_open: isOpen,
      is_24_hours: is24Hours,
      shifts: isOpen ? this.prepareShiftsForPayload(today) : [],
      reason: this.overrideReason || null
    };

    this.saving = true;

    this.posService.setEmergencyOpeningHourOverride(this.warehouseId, payload).subscribe({
      next: (res: any) => {
        this.savedSchedule = this.cloneSchedule(this.draftSchedule);
        this.effectiveSource = 'override';
        this.viewMode = 'form';
        this.submitted = false;
        this.saving = false;

        Swal.fire({
          icon: 'success',
          title: this.isAr ? 'تم حفظ التغيير الاضطراري' : 'Emergency shift saved',
          text: this.isAr ? 'التغيير ده لليوم المحدد فقط' : 'This change applies to the selected day only',
          timer: 1400,
          showConfirmButton: false
        });
      },
      error: (error) => {
        this.saving = false;

        this.serverError =
          error?.error?.message ||
          this.t('messages.saveFailed');

        Swal.fire({
          icon: 'error',
          title: this.t('messages.errorTitle'),
          text: this.serverError
        });
      }
    });
  }

  deleteTodayOverride(): void {
    if (this.saving) return;

    this.saving = true;
    this.posService.deleteEmergencyOpeningHourOverride(this.warehouseId, this.overrideDate).subscribe({
      next: () => {
        this.saving = false;
        this.overrideReason = '';
        this.getOpeningHours();

        Swal.fire({
          icon: 'success',
          title: this.isAr ? 'تم إلغاء التغيير الاضطراري' : 'Emergency change removed',
          timer: 1200,
          showConfirmButton: false
        });
      },
      error: (error) => {
        this.saving = false;
        this.serverError =
          error?.error?.message ||
          (this.isAr ? 'تعذر إلغاء التغيير الاضطراري' : 'Failed to remove emergency change');
      }
    });
  }

  isScheduleValid(): boolean {
    return this.draftSchedule.every((day) => {
      if (!day.enabled) return true;

      if (day.is24Hours) {
        return true;
      }

      if (!day.shifts.length) return false;

      return day.shifts.every((shift) => {
        return Boolean(shift.start && shift.end && shift.start !== shift.end);
      });
    });
  }

  hasDayError(day: DaySchedule): boolean {
    if (!this.submitted || !day.enabled) return false;

    if (day.is24Hours) {
      return false;
    }

    if (!day.shifts.length) return true;

    return day.shifts.some((shift) => {
      return !shift.start || !shift.end || shift.start === shift.end;
    });
  }

  getScheduleText(day: DaySchedule): string {
    if (!day.enabled) return '';

    if (day.is24Hours) {
      return this.isAr ? '٢٤ ساعة' : '24 hours';
    }

    if (!day.shifts.length) return '';

    return day.shifts
      .map((shift) => this.formatShiftText(shift))
      .join(' / ');
  }

  formatShiftText(shift: OpeningShift): string {
    return `${this.formatTime(shift.start)} - ${this.formatTime(shift.end)}`;
  }

  formatTime(time: string): string {
    if (!time) return '';

    const [hourText, minuteText] = time.split(':');
    const hour = Number(hourText);
    const minute = Number(minuteText);

    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return time;
    }

    const period = hour < 12 ? 'morning' : 'evening';
    const hour12 = hour % 12 || 12;
    const formattedTime = `${hour12.toString().padStart(2, '0')}:${minute
      .toString()
      .padStart(2, '0')}`;

    if (!this.isAr) {
      return time;
    }

    return `${this.toArabicDigits(formattedTime)} ${
      period === 'morning' ? 'صباحًا' : 'مساءً'
    }`;
  }

  getTimeHour12(time: string): number {
    const hour = Number((time || '10:00').split(':')[0]);

    if (Number.isNaN(hour)) {
      return 10;
    }

    return hour % 12 || 12;
  }

  getTimeMinute(time: string): string {
    const minute = (time || '10:00').split(':')[1] || '00';
    return minute.padStart(2, '0');
  }

  getTimePeriod(time: string): 'am' | 'pm' {
    const hour = Number((time || '10:00').split(':')[0]);
    return !Number.isNaN(hour) && hour >= 12 ? 'pm' : 'am';
  }

  getTimeOptionLabel(value: string | number): string {
    return this.isAr ? this.toArabicDigits(value) : String(value);
  }

  getPeriodOptionLabel(period: 'am' | 'pm'): string {
    if (!this.isAr) {
      return period.toUpperCase();
    }

    return period === 'am' ? 'صباحًا' : 'مساءً';
  }

  toArabicDigits(value: string | number): string {
    return String(value).replace(/\d/g, (digit) => '٠١٢٣٤٥٦٧٨٩'[Number(digit)]);
  }

  isOvernightShift(shift: OpeningShift): boolean {
    if (!shift.start || !shift.end) return false;
    return shift.end < shift.start;
  }

  isUserSelected(shift: OpeningShift, userId: number): boolean {
    return (shift.user_ids || []).map(Number).includes(Number(userId));
  }

  getShiftUserIds(shift: OpeningShift): number[] {
    return this.uniqueIds(shift.user_ids || []);
  }

  getUserName(id: number | string): string {
    const numericId = Number(id);
    const user =
      this.productname.find((item) => Number(item.id) === numericId) ||
      this.usersMap.get(numericId);

    return user ? this.getUserOptionLabel(user) : '';
  }

  getUserDisplayName(user: ClinicUser | null | undefined): string {
    if (!user) return '-';

    const firstName = String(user?.first_name || '').trim();
    const fullName = [user?.first_name, user?.last_name]
      .filter(Boolean)
      .join(' ')
      .trim();

    return (
      firstName ||
      String(user?.first_name || '').trim() ||
      String(user?.name || '').trim() ||
      String(user?.name_en || '').trim() ||
      fullName ||
      String(user?.email || '').trim() ||
      `${this.isAr ? 'مستخدم' : 'User'} #${user.id}`
    );
  }

  getUserWarehouseName(user: any): string {
    const warehouseFields = [
      user?.warehouse?.name,
      user?.warehouse?.name_ar,
      user?.pos?.name,
      user?.pos?.name_ar,
      user?.branch?.name,
      user?.branch?.name_ar,
      user?.store?.name,
      user?.store?.name_ar,
      user?.warehouse_name,
      user?.warehouse_name_ar,
      user?.warehouses_name,
      user?.pos_name,
      user?.branch_name,
      user?.store_name,
    ];

    const directWarehouse = warehouseFields.find((value) => String(value || '').trim());
    if (directWarehouse) {
      return String(directWarehouse).trim();
    }

    const warehouses = user?.warehouses || user?.warehouse_users || user?.user_warehouses;
    if (Array.isArray(warehouses)) {
      const warehouseNames = warehouses
        .map((warehouse: any) =>
          warehouse?.name ||
          warehouse?.name_ar ||
          warehouse?.warehouse?.name ||
          warehouse?.warehouse?.name_ar
        )
        .filter((name: any) => String(name || '').trim())
        .join(', ');

      if (warehouseNames) {
        return warehouseNames;
      }
    }

    const firstName = String(user?.first_name || '').trim().replace(/\s+/g, ' ').toLowerCase();
    const lastName = String(user?.last_name || '').trim();
    const normalizedLastName = lastName.replace(/\s+/g, ' ').toLowerCase();

    if (lastName && normalizedLastName !== firstName) {
      return lastName;
    }

    return '';
  }

  getUserOptionLabel(user: any): string {
    const displayName = this.getUserDisplayName(user);
    const warehouseName = this.getUserWarehouseName(user);

    return warehouseName ? `${displayName} (${warehouseName})` : displayName;
  }

  getUserMeta(user: any): string {
    const values = [
      user?.username,
      user?.phone,
      user?.phonenumber,
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean);

    return Array.from(new Set(values)).join(' - ');
  }

  onUserSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.userSearchValue = input.value;
    const search = this.userSearchValue.trim().toLowerCase();

    if (!search) {
      this.filteredProviders = [...this.productname];
      return;
    }

    this.filteredProviders = this.productname.filter((user: any) => {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
      const reverseName = `${user.last_name || ''} ${user.first_name || ''}`.toLowerCase();
      const username = String(user.username || '').toLowerCase();
      const phone = String(user.phone || user.phonenumber || '').toLowerCase();
      const warehouseName = this.getUserWarehouseName(user).toLowerCase();

      return (
        fullName.includes(search) ||
        reverseName.includes(search) ||
        username.includes(search) ||
        phone.includes(search) ||
        warehouseName.includes(search)
      );
    });
  }

  clearUserSearch(event?: Event): void {
    event?.stopPropagation();
    this.userSearchValue = '';
    this.filteredProviders = [...this.productname];
  }

  getUserInitial(user: ClinicUser): string {
    const name = this.getUserDisplayName(user);
    return name.trim().charAt(0).toUpperCase() || 'U';
  }

  getShiftUserNames(shift: OpeningShift): string[] {
    const ids = this.uniqueIds(shift.user_ids || []);

    return ids.map((id) => {
      const user =
        this.usersMap.get(Number(id)) ||
        (shift.users || []).find((item) => Number(item.id) === Number(id));

      return this.getUserDisplayName(user);
    });
  }

  getSelectedUsersLabel(shift: OpeningShift): string {
    const count = this.uniqueIds(shift.user_ids || []).length;

    if (this.isAr) {
      if (count === 0) return 'لم يتم اختيار مستخدمين';
      if (count === 1) return 'مستخدم واحد';
      return `${this.toArabicDigits(count)} مستخدمين`;
    }

    if (count === 0) return 'No users selected';
    if (count === 1) return '1 user selected';
    return `${count} users selected`;
  }

  openUsersPopup(day: DaySchedule, shiftIndex: number): void {
    if (this.usersLoading || !day.shifts[shiftIndex]) return;

    this.popupDay = day;
    this.popupShiftIndex = shiftIndex;
    this.popupTempUserIds = this.getShiftUserIds(day.shifts[shiftIndex]);
    this.userSearchValue = '';
    this.filteredProviders = [...this.productname];
    this.usersPopupOpen = true;
  }

  closeUsersPopup(): void {
    this.usersPopupOpen = false;
    this.popupDay = null;
    this.popupShiftIndex = -1;
    this.popupTempUserIds = [];
    this.userSearchValue = '';
    this.filteredProviders = [...this.productname];
  }

  saveUsersPopup(): void {
    if (!this.popupDay || this.popupShiftIndex < 0) return;

    this.updateShiftUsers(this.popupDay, this.popupShiftIndex, this.popupTempUserIds);
    this.closeUsersPopup();
  }

  togglePopupUser(userId: number | string, event: Event): void {
    const id = Number(userId);
    const checked = (event.target as HTMLInputElement).checked;

    if (Number.isNaN(id)) return;

    if (checked) {
      this.popupTempUserIds = this.uniqueIds([...this.popupTempUserIds, id]);
      return;
    }

    this.popupTempUserIds = this.popupTempUserIds.filter((item) => Number(item) !== id);
  }

  isPopupUserChecked(userId: number | string): boolean {
    const id = Number(userId);
    return this.popupTempUserIds.some((item) => Number(item) === id);
  }

  removePopupSelectedUser(userId: number | string, event?: Event): void {
    event?.stopPropagation();

    const id = Number(userId);
    this.popupTempUserIds = this.popupTempUserIds.filter((item) => Number(item) !== id);
  }

  getPopupSelectedUsersLabel(): string {
    const count = this.popupTempUserIds.length;

    if (this.isAr) {
      if (count === 0) return 'لم يتم اختيار موظفين';
      if (count === 1) return 'تم اختيار موظف واحد';
      return `تم اختيار ${this.toArabicDigits(count)} موظفين`;
    }

    if (count === 0) return 'No employees selected';
    if (count === 1) return '1 employee selected';
    return `${count} employees selected`;
  }

  trackByDay(index: number, item: DaySchedule): DayKey {
    return item.day;
  }

  trackByShift(index: number): number {
    return index;
  }

  trackByUser(index: number, user: ClinicUser): number {
    return Number(user.id);
  }

  private prepareShiftsForPayload(day: DaySchedule): OpeningShift[] {
    if (!day.enabled) return [];

    if (day.is24Hours) {
      const userIds = this.uniqueIds(day.shifts[0]?.user_ids || []);

      return [
        {
          start: '00:00',
          end: '23:59',
          user_ids: userIds
        }
      ];
    }

    return day.shifts.map((shift) => ({
      start: this.normalizeTime(shift.start),
      end: this.normalizeTime(shift.end),
      user_ids: this.uniqueIds(shift.user_ids || [])
    }));
  }

  private t(key: string): string {
    return this.transloco.translate(`clinicAppointments.${key}`);
  }

  private extractScheduleFromResponse(res: any): BackendOpeningHour[] {
    if (Array.isArray(res?.data)) {
      return res.data;
    }

    if (Array.isArray(res?.schedule)) {
      return res.schedule;
    }

    if (Array.isArray(res?.opening_hours)) {
      return res.opening_hours;
    }

    if (Array.isArray(res)) {
      return res;
    }

    return [];
  }

  private extractEffectiveOpeningHour(res: any): BackendOpeningHour & { reason?: string } {
    const data = res?.data || res?.effective || res?.opening_hour || res || {};
    const nested = data?.opening_hour || data?.schedule || data;
    const day = nested?.day || data?.day || this.getDayKeyFromDate(this.parseDateOnly(this.overrideDate) || new Date());

    return {
      ...nested,
      day,
      is_open: nested?.is_open ?? data?.is_open ?? true,
      is_24_hours: nested?.is_24_hours ?? nested?.is24hours ?? data?.is_24_hours ?? data?.is24hours ?? false,
      shifts: nested?.shifts ?? data?.shifts ?? [],
      reason: nested?.reason ?? data?.reason ?? res?.reason ?? '',
    };
  }

  private extractEffectiveSource(res: any): 'default' | 'override' {
    const source = String(res?.source || res?.data?.source || res?.effective?.source || '').toLowerCase();
    return source === 'override' ? 'override' : 'default';
  }

  private normalizeEffectiveDay(row: BackendOpeningHour): DaySchedule {
    const day = row?.day || this.getDayKeyFromDate(this.parseDateOnly(this.overrideDate) || new Date());
    const isOpen = this.toBoolean(row?.is_open);
    const shifts = this.extractShiftsFromRow(row).filter((shift) => shift.start && shift.end);
    const explicit24Hours = this.toBoolean(row?.is_24_hours ?? row?.is24hours ?? false);
    const inferred24Hours = shifts.some((shift) => {
      return shift.start === '00:00' && ['23:59', '24:00'].includes(shift.end);
    });
    const is24Hours = isOpen && (explicit24Hours || inferred24Hours);

    return {
      day,
      enabled: isOpen,
      is24Hours,
      shifts: isOpen ? this.ensureShiftsForDay(is24Hours, shifts) : []
    };
  }

  private extractUsersFromResponse(res: any): ClinicUser[] {
    if (Array.isArray(res?.users)) {
      return res.users;
    }

    if (Array.isArray(res?.data)) {
      return res.data;
    }

    if (Array.isArray(res)) {
      return res;
    }

    return [];
  }

  private userBelongsToWarehouse(user: any, warehouseId: number): boolean {
    if (!warehouseId || Number.isNaN(warehouseId)) {
      return true;
    }

    const directIds = [
      user?.warehouse_id,
      user?.warehouseId,
      user?.warehouse?.id,
      user?.warehouse?.warehouse_id,
      user?.pos_id,
      user?.posId,
      user?.pos?.id,
      user?.branch_id,
      user?.branch?.warehouse_id,
      user?.store_id,
      user?.store?.warehouse_id,
      user?.pivot?.warehouse_id,
      user?.pivot?.warehouseId
    ];

    if (directIds.some((id) => Number(id) === warehouseId)) {
      return true;
    }

    const arrayIds = [
      user?.warehouse_ids,
      user?.warehouseIds,
      user?.warehouses_ids
    ];

    if (
      arrayIds.some((list) =>
        Array.isArray(list) && list.some((id) => Number(id) === warehouseId)
      )
    ) {
      return true;
    }

    const relationLists = [
      user?.warehouses,
      user?.warehouse_users,
      user?.user_warehouses,
      user?.branches,
      user?.stores
    ];

    return relationLists.some((list) => {
      if (!Array.isArray(list)) return false;

      return list.some((item: any) => {
        const ids = [
          item?.id,
          item?.warehouse_id,
          item?.warehouseId,
          item?.warehouse?.id,
          item?.warehouse?.warehouse_id,
          item?.pivot?.warehouse_id,
          item?.pivot?.warehouseId,
          item?.store?.warehouse_id,
          item?.branch?.warehouse_id
        ];

        return ids.some((id) => Number(id) === warehouseId);
      });
    });
  }

  private normalizeSchedule(apiSchedule: BackendOpeningHour[]): DaySchedule[] {
    const scheduleMap = new Map<DayKey, DaySchedule>();

    this.daysOrder.forEach((day) => {
      const dayRows = apiSchedule
        .filter((item) => item.day === day)
        .sort((a, b) => Number(a.shift_order || 0) - Number(b.shift_order || 0));

      if (!dayRows.length) {
        scheduleMap.set(day, {
          day,
          enabled: false,
          is24Hours: false,
          shifts: []
        });

        return;
      }

      const firstRow = dayRows[0];
      const isOpen = this.toBoolean(firstRow.is_open);

      const shifts = dayRows
        .flatMap((row) => this.extractShiftsFromRow(row))
        .filter((shift) => shift.start && shift.end);

      const explicit24Hours = this.toBoolean(
        firstRow.is_24_hours ?? firstRow.is24hours ?? false
      );

      const inferred24Hours = shifts.some((shift) => {
        return shift.start === '00:00' && ['23:59', '24:00'].includes(shift.end);
      });

      const is24Hours = isOpen && (explicit24Hours || inferred24Hours);

      scheduleMap.set(day, {
        day,
        enabled: isOpen,
        is24Hours,
        shifts: isOpen ? this.ensureShiftsForDay(is24Hours, shifts) : []
      });
    });

    return this.daysOrder.map((day) => {
      return (
        scheduleMap.get(day) || {
          day,
          enabled: false,
          is24Hours: false,
          shifts: []
        }
      );
    });
  }

  private ensureShiftsForDay(is24Hours: boolean, shifts: OpeningShift[]): OpeningShift[] {
    if (is24Hours) {
      const userIds = this.uniqueIds(shifts.flatMap((shift) => shift.user_ids || []));

      return [
        {
          start: '00:00',
          end: '23:59',
          user_ids: userIds,
          users: this.getUsersByIds(userIds)
        }
      ];
    }

    return shifts;
  }

  private extractShiftsFromRow(row: BackendOpeningHour): OpeningShift[] {
    const shiftsFromJson = this.parseShifts(row.shifts);

    if (shiftsFromJson.length > 0) {
      return shiftsFromJson;
    }

    if (row.opens_at && row.closes_at) {
      return [
        {
          start: this.normalizeTime(row.opens_at),
          end: this.normalizeTime(row.closes_at),
          user_ids: []
        }
      ];
    }

    return [];
  }

  private parseShifts(shifts: any): OpeningShift[] {
    if (!shifts) return [];

    let parsedShifts: any[] = [];

    if (Array.isArray(shifts)) {
      parsedShifts = shifts;
    } else {
      try {
        const parsed = JSON.parse(shifts);

        if (Array.isArray(parsed)) {
          parsedShifts = parsed;
        }
      } catch {
        parsedShifts = [];
      }
    }

    return parsedShifts
      .map((shift) => {
        const users = Array.isArray(shift?.users) ? shift.users : [];
        this.registerUsers(users);

        const userIdsFromUsers = users
          .map((user: ClinicUser) => Number(user.id))
          .filter((id: number) => !Number.isNaN(id));

        const userIds = this.normalizeUserIds(
          shift?.user_ids ?? shift?.userIds ?? userIdsFromUsers
        );

        return {
          start: this.normalizeTime(shift?.start),
          end: this.normalizeTime(shift?.end),
          user_ids: userIds,
          users
        };
      })
      .filter((shift) => shift.start && shift.end);
  }

  private hasAnyRealSavedTime(schedule: DaySchedule[]): boolean {
    return schedule.some((day) => {
      return day.enabled && day.shifts.some((shift) => shift.start && shift.end);
    });
  }

  private registerUsers(users: ClinicUser[]): void {
    users.forEach((user) => {
      const id = Number(user?.id);

      if (!Number.isNaN(id)) {
        this.usersMap.set(id, user);
      }
    });
  }

  private getUsersByIds(ids: number[]): ClinicUser[] {
    return ids
      .map((id) => this.usersMap.get(Number(id)))
      .filter(Boolean) as ClinicUser[];
  }

  private normalizeUserIds(value: any): number[] {
    if (!Array.isArray(value)) return [];

    return this.uniqueIds(
      value
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id))
    );
  }

  private uniqueIds(ids: number[]): number[] {
    return Array.from(new Set(ids.map((id) => Number(id))));
  }

  private toBoolean(value: boolean | number | string | null | undefined): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private normalizeTime(time: string | null | undefined): string {
    if (!time) return '';
    return String(time).substring(0, 5);
  }

  private to24Hour(hour12: number, period: string): string {
    const normalizedHour =
      period === 'pm'
        ? (hour12 % 12) + 12
        : hour12 % 12;

    return normalizedHour.toString().padStart(2, '0');
  }

  private parseDateOnly(value: string): Date | null {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  private formatDateOnly(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private getDayKeyFromDate(date: Date): DayKey {
    return this.daysOrder[date.getDay()];
  }

  private createDefaultDaySchedule(day: DayKey): DaySchedule {
    const defaultDay = this.createDefaultSchedule().find((item) => item.day === day);
    return defaultDay || {
      day,
      enabled: true,
      is24Hours: false,
      shifts: [{ start: '10:00', end: '22:00', user_ids: [] }]
    };
  }

  private createDefaultSchedule(): DaySchedule[] {
    return [
      {
        day: 'sunday',
        enabled: true,
        is24Hours: false,
        shifts: [{ start: '10:00', end: '22:00', user_ids: [] }]
      },
      {
        day: 'monday',
        enabled: true,
        is24Hours: false,
        shifts: [{ start: '10:00', end: '22:00', user_ids: [] }]
      },
      {
        day: 'tuesday',
        enabled: true,
        is24Hours: false,
        shifts: [{ start: '10:00', end: '22:00', user_ids: [] }]
      },
      {
        day: 'wednesday',
        enabled: true,
        is24Hours: false,
        shifts: [{ start: '10:00', end: '22:00', user_ids: [] }]
      },
      {
        day: 'thursday',
        enabled: true,
        is24Hours: false,
        shifts: [{ start: '10:00', end: '22:00', user_ids: [] }]
      },
      {
        day: 'friday',
        enabled: false,
        is24Hours: false,
        shifts: []
      },
      {
        day: 'saturday',
        enabled: true,
        is24Hours: false,
        shifts: [{ start: '10:00', end: '01:00', user_ids: [] }]
      }
    ];
  }

  private cloneSchedule(schedule: DaySchedule[]): DaySchedule[] {
    return schedule.map((item) => ({
      ...item,
      shifts: item.shifts.map((shift) => ({
        ...shift,
        user_ids: [...(shift.user_ids || [])],
        users: shift.users ? [...shift.users] : undefined
      }))
    }));
  }
}
